/**
 * A 表 · 认证域 —— 9 个端点**全部已建成**。
 *
 * ⚠ 形状以 identity 的 `AuthDtos.java` / `AuthController.java` 为准，
 *   **不是**以契约文字为准 —— 2026-09-01 逐个核对时发现三处不一致，
 *   运行的代码才是真相源。三处都在下面标了，已反馈后端补契约：
 *
 *   1. `userId` 是 JSON **字符串**，不是 number(见 {@link userSchema})；
 *   2. 登录类响应是**嵌套**的 `{ tokens, user }`，不是契约写的「平铺 TokenPair + User」；
 *   3. **A5 refresh 实际回整个 `AuthResponse`(含 user)**，契约写的是只回 `TokenPair`。
 */
import { z } from 'zod'

export const USER_LEVELS = ['ANON', 'USER', 'ADMIN'] as const
/**
 * ⚠ 严格三选一，不做兜底。多出来的值意味着契约被破坏，
 *   而身份等级判错的后果是越权 —— 宁可解析失败炸出来，也不要静默降级或升级。
 */
export const userLevelSchema = z.enum(USER_LEVELS)
export type UserLevel = z.infer<typeof userLevelSchema>

/**
 * ⚠⚠ `userId` 是**字符串**。
 *
 * 契约写「`userId` 是数字形态」指的是**内容**能转 long(conversation 侧强校验)，
 * **不是** JSON 类型为 number —— identity 的 `UserView.userId` 是 Java `String`，
 * 建表注释也写着「雪花 id，对前端序列化为字符串」。
 *
 * 这一处按字面读成 `z.number()` 的后果不是解析失败那么轻：雪花 id 超过 2^53，
 * 真当 number 收会**静默丢精度**，两个不同用户可能算成同一个人。
 *
 * ⚠ 其余四个字段 Java 侧都是 `String`，匿名用户的 `phoneMasked` 为空 —— 都要容忍 null。
 */
export const userSchema = z.looseObject({
  userId: z.string().regex(/^\d+$/, 'userId 必须是数字形态的雪花 id'),
  level: userLevelSchema,
  phoneMasked: z.string().nullable().default(null),
  nickname: z.string().nullable().default(null),
  avatarUrl: z.string().nullable().default(null),
})
export type User = z.infer<typeof userSchema>

/**
 * ⚠ `accessToken` **5 分钟**过期(后端故意不做黑名单)。
 *   HTTP 层必须做单飞刷新：并发多个请求同时撞到过期，只能刷一次，其余排队。
 *
 * `refreshToken` 是不透明随机串(= access 的 jti)，30 天，存 Redis。
 * `expiresIn` 是 accessToken 的**剩余秒数**，不是绝对时间戳。
 */
export const tokenPairSchema = z.looseObject({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().nonnegative(),
})
export type TokenPair = z.infer<typeof tokenPairSchema>

/** A1 领匿名 / A3 登录 / A4 管理员登录 / **A5 续期** 的统一返回。 */
export const authResponseSchema = z.looseObject({
  tokens: tokenPairSchema,
  user: userSchema,
})
export type AuthResponse = z.infer<typeof authResponseSchema>

// ---------------- 请求 ----------------

/** A2 的场景：`login` 登录/注册，`bind` 设置或修改密码。空则按 login。 */
export const SMS_SCENES = ['login', 'bind'] as const
export const smsSceneSchema = z.enum(SMS_SCENES)
export type SmsScene = z.infer<typeof smsSceneSchema>

export const sendCodeRequestSchema = z.looseObject({
  countryCode: z.string().max(8).optional(),
  phone: z.string().min(1).max(32),
  scene: smsSceneSchema.optional(),
})
export type SendCodeRequest = z.infer<typeof sendCodeRequestSchema>

export const sendCodeResponseSchema = z.looseObject({
  cooldownSeconds: z.number().int().nonnegative(),
})
export type SendCodeResponse = z.infer<typeof sendCodeResponseSchema>

/**
 * A3 登录。
 *
 * ⚠ `smsCode` 与 `password` **二选一**：两个都给或都不给都会被服务端拒。
 *   都给的话服务端不知道该信哪个，而「两个里对一个就放行」等于把两条路的
 *   失败次数分开算，削弱了各自的限频。这条是服务端已有的规则，前端照着挡一道，
 *   省一次往返。
 *
 * ⚠⚠ 调用时**必须带上匿名令牌**(如果有) —— 那是在要求合并影子用户。
 *   不合并的话，匿名期搜的机票报价快照还挂在影子 id 上，登录后取料直接被拒，
 *   用户被迫重搜(16 秒起)。这条约束在 HTTP 层，schema 挡不住，见 api-client。
 */
export const loginRequestSchema = z
  .looseObject({
    countryCode: z.string().max(8).optional(),
    phone: z.string().min(1).max(32),
    smsCode: z.string().max(16).optional(),
    password: z.string().max(128).optional(),
  })
  .refine((v) => Boolean(v.smsCode) !== Boolean(v.password), {
    message: 'smsCode 与 password 二选一，不能都给也不能都不给',
  })
export type LoginRequest = z.infer<typeof loginRequestSchema>

export const adminLoginRequestSchema = z.looseObject({
  username: z.string().min(1).max(320),
  password: z.string().min(1).max(128),
})
export type AdminLoginRequest = z.infer<typeof adminLoginRequestSchema>

/** A5 续期与 A6 登出共用。 */
export const refreshRequestSchema = z.looseObject({
  refreshToken: z.string().min(1).max(128),
})
export type RefreshRequest = z.infer<typeof refreshRequestSchema>

/** A8 改资料。⚠ 字段为 null 表示「不改」，**不是**「清空」。 */
export const updateMeRequestSchema = z.looseObject({
  nickname: z.string().max(100).optional(),
  avatarUrl: z.string().max(512).optional(),
})
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>

/**
 * A9 改密。⚠ 需 `bind` 场景的验证码二次确认：光有 access 令牌不够 ——
 * 令牌可能来自一台没锁屏的手机，而改密码等于把账号永久交出去。
 * ⚠ **匿名禁止**调用本端点(网关拦)。
 */
export const setPasswordRequestSchema = z.looseObject({
  smsCode: z.string().min(1).max(16),
  newPassword: z.string().min(8).max(128),
})
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>
